import { styled } from "@linaria/react";
import React from "react";
import { ACTIVE_CN, DISABLED_CN } from "./Tab";
import useObserverCallback from "../lib/hooks/useObserverCallback";
import { color } from "..";
import { css } from "@linaria/core";
import cx from "classnames";
import useEnsuredForwardedRef from "../lib/hooks/useEnsuredForwardedRef";

const Marker = styled.div<{ thin?: boolean }>`
  height: 2px;
  background-color: ${color.lightNitrogen500_Main};
`;

const TabContainer = css`
  display: grid;
  grid-auto-columns: max-content;
  grid-auto-flow: column;
  grid-template-rows: 1fr;
  position: relative;

  > :not(${Marker}) {
    grid-row: 1 / span 1;
  }

  a {
    user-select: none;
  }

  ${Marker} {
    position: absolute;
    bottom: 0;
    left: 0;
    opacity: 0;
    will-change: transform;
    transform-origin: left;
    transition:
      width 350ms cubic-bezier(0.4, 0, 0.2, 1),
      transform 350ms cubic-bezier(0.4, 0, 0.2, 1);

    &.no-transition {
      transition: none;
    }
  }
`;

interface P extends React.HTMLAttributes<HTMLDivElement> {
  children: React.ReactNode;
  thin?: boolean;
  reactiveProp?: string;
}

const getAbsoluteOffsetLeft = (el: HTMLElement | null): number => {
  let offset = 0;
  while (el) {
    offset += el.offsetLeft;
    el = el.offsetParent as HTMLElement;
  }
  return offset;
};

const Nav = (
  { children, className, reactiveProp, thin = false }: P,
  ref: React.ForwardedRef<HTMLDivElement>,
) => {
  const wrapperRef = useEnsuredForwardedRef(ref);
  const markerRef = React.useRef<HTMLDivElement>(null);

  const handleKeyDown = React.useCallback(
    (e: React.KeyboardEvent<HTMLDivElement>) => {
      if (!wrapperRef.current) throw new Error("Missing wrapper ref");
      if (["ArrowRight", "ArrowLeft"].includes(e.key)) {
        e.preventDefault();

        const activables = Array.from(wrapperRef.current.children).filter(
          el => el.tagName === "A" && !el.classList.contains(DISABLED_CN),
        ) as HTMLAnchorElement[];
        const active = activables.find(a => a.classList.contains(ACTIVE_CN));

        const nextSibling = (
          e.key === "ArrowRight"
            ? active?.nextElementSibling
            : e.key === "ArrowLeft"
              ? active?.previousElementSibling
              : null
        ) as HTMLAnchorElement | null;

        const next =
          nextSibling && activables.includes(nextSibling)
            ? nextSibling
            : activables[
                e.key === "ArrowRight" ? 0 : e.key === "ArrowLeft" ? activables.length - 1 : -1
              ];

        next?.click();
      }
    },
    [wrapperRef],
  );

  const update = React.useCallback(() => {
    const wrapper = wrapperRef.current;
    const marker = markerRef.current;
    if (!wrapper || !marker) throw new Error("missing ref");

    const activeEls = wrapper.querySelectorAll('a[class*="TabItem"].active');
    const activeLink = activeEls[0] || null;

    if (activeLink === null || activeLink.classList.contains(DISABLED_CN)) {
      marker.style.opacity = "0";
      return;
    }

    const offsetX =
      getAbsoluteOffsetLeft(activeLink as HTMLElement) - getAbsoluteOffsetLeft(wrapper);

    marker.style.transform = `translateX(${offsetX}px)`;
    marker.style.width = `${(activeLink as HTMLElement).offsetWidth}px`;

    marker.style.opacity = "1";
    if (marker.classList.contains("no-transition"))
      window.requestAnimationFrame(() => marker.classList.remove("no-transition"));
  }, [wrapperRef]);

  React.useLayoutEffect(update, [reactiveProp, children, update]);
  useObserverCallback(update, [update], wrapperRef, { fireOnInit: false, debounced: true });

  return (
    <nav
      tabIndex={0}
      className={cx(TabContainer, className)}
      ref={wrapperRef}
      onKeyDown={handleKeyDown}
    >
      {children}
      <Marker ref={markerRef} thin={thin} className="no-transition" />
    </nav>
  );
};

export default React.memo(React.forwardRef(Nav));
